import { useState, useCallback } from 'react';
import useApiFetch from '@/hooks/useApiFetch';
import interviewTechnicalChallengesService from '@/services/interviewTechnicalChallenges';
import { FetchApiIds } from '@/types';
import { InterviewTechnicalChallenge } from '@/types/api';
import { InterviewChallengeRequestStatusFilter } from '@/types/api/interviewTechnicalChallenge';
import ChallengeManagementComponent from './ChallengeManagement.component';
import EditTechnicalChallengeModal from './EditTechnicalChallengeModal';

const ChallengeManagementContainer = (): JSX.Element => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<InterviewChallengeRequestStatusFilter>(
    InterviewChallengeRequestStatusFilter.UNREVIEWED
  );
  const [editingChallenge, setEditingChallenge] = useState<InterviewTechnicalChallenge | null>(
    null
  );

  const {
    data: challengesResponse,
    isLoading,
    refetch,
  } = useApiFetch(
    [FetchApiIds.INTERVIEW_TECHNICAL_CHALLENGES, currentPage, itemsPerPage, statusFilter],
    async () => {
      const params: any = {
        page: currentPage,
        take: itemsPerPage,
        status: statusFilter,
      };

      return interviewTechnicalChallengesService.listInterviewTechnicalChallenges(params);
    }
  );

  const challenges = challengesResponse?.data || [];
  const count = challengesResponse?.count || 0;
  const lastPage = challengesResponse?.lastPage || 1;

  const onChangePageHandler = useCallback((page: number): void => {
    setCurrentPage(page);
  }, []);

  const onChangeRecordsPerPage = useCallback((recordsPerPage: number): void => {
    setItemsPerPage(recordsPerPage);
    setCurrentPage(1);
  }, []);

  const onStatusFilterChange = useCallback(
    (status: InterviewChallengeRequestStatusFilter): void => {
      setStatusFilter(status);
      setCurrentPage(1);
    },
    []
  );

  const handleEditChallenge = useCallback((challenge: InterviewTechnicalChallenge): void => {
    setEditingChallenge(challenge);
  }, []);

  const handleCloseEditModal = useCallback((): void => {
    setEditingChallenge(null);
  }, []);

  const handleChallengeUpdated = useCallback(
    (updatedChallenge: InterviewTechnicalChallenge): void => {
      // Refetch the challenges to get the updated data
      refetch();
      handleCloseEditModal();
    },
    [handleCloseEditModal, refetch]
  );

  return (
    <>
      <ChallengeManagementComponent
        challenges={challenges}
        isLoading={isLoading}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        count={count}
        lastPage={lastPage}
        onChangePageHandler={onChangePageHandler}
        onChangeRecordsPerPage={onChangeRecordsPerPage}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        onEditChallenge={handleEditChallenge}
      />

      {editingChallenge && (
        <EditTechnicalChallengeModal
          isOpen={!!editingChallenge}
          onClose={handleCloseEditModal}
          challenge={editingChallenge}
          onChallengeUpdated={handleChallengeUpdated}
          onReviewersChanged={refetch}
        />
      )}
    </>
  );
};

export default ChallengeManagementContainer;
